import {
    type Availability,
    availabilityColor,
    availabilityIcon,
    availabilityLabel,
    AvailabilityStatus,
    formatDate,
    formatWeekDayShort,
    Repeat,
    toTimeString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import type { TimeInput } from "@pentacode/ui/components";
import { css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { app } from "../init";
import { alert, confirm } from "./alert-dialog";
import { Checkbox } from "./checkbox";
import { DateInput } from "./date-input";
import { Dialog } from "./dialog";
import "./popover";

@customElement("ptc-availability-dialog")
export class AvailabilityDialog extends Dialog<Availability, boolean> {
    readonly preventDismiss = true;

    @query("ptc-ui-time-input[name='start']")
    private _startInput: TimeInput;

    @query("ptc-ui-time-input[name='end']")
    private _endInput: TimeInput;

    @query("textarea[name='comment']")
    private _commentInput: HTMLTextAreaElement;

    @query("select[name='repeats']")
    private _repeatsSelect: HTMLSelectElement;

    @state()
    private _availability: Availability;

    @state()
    private _fullDay = true;

    @state()
    private _status: AvailabilityStatus;

    async show(av?: Availability) {
        const promise = super.show();

        if (!av) {
            return;
        }

        this._availability = av;
        this._status = this._availability.status;
        this._fullDay = !this._availability.start && !this._availability.end;

        await this.updateComplete;

        this._startInput.value = this._availability.start || "";
        this._endInput.value = this._availability.end || "";
        this._repeatsSelect.value = this._availability.repeats;
        this._commentInput.value = this._availability.comment;

        // TODO: fill form

        return promise;
    }

    private _fullDayToggled(e: Event) {
        this._fullDay = (e.target as Checkbox).checked;
    }

    private async _submit(e: FocusEvent) {
        e.preventDefault();

        const av = this._availability;

        av.start = this._fullDay ? null : this._startInput.value;
        av.end = this._fullDay ? null : this._endInput.value;
        av.comment = this._commentInput.value;
        av.repeats = this._repeatsSelect.value as Repeat;
        av.status = this._status;

        this.loading = true;

        try {
            av.id ? await app.api.updateAvailability(av) : await app.api.createAvailability(av);
            this.done(true);
        } catch (err) {
            this.hide();
            await alert(err.message, { type: "warning" });
            void this.show();
        }

        this.loading = false;
    }

    private async _delete() {
        this.hide();
        if (
            !(await confirm("Wollen Sie diese Verfügbarkeit wirklich entfernen?", "Entfernen", "Abbrechen", {
                title: "Verfügbarkeit Entfernen",
                type: "destructive",
            }))
        ) {
            this._show();
            return;
        }
        this._show();
        this.loading = true;
        try {
            await app.api.deleteAvailability(this._availability.id);
            this.done(true);
        } catch (err) {
            this.hide();
            await alert(err.message, { type: "warning" });
            this._show();
        }
        this.loading = false;
    }

    static styles = [
        ...Dialog.styles,
        DateInput.styles,
        Checkbox.styles,
        css`
            :host {
                --dialog-max-width: 400px;
            }
        `,
    ];

    renderContent() {
        if (!this._availability) {
            return html``;
        }

        // const today = toDateString(new Date());
        const editing = !!this._availability.id;
        const date = this._availability.date;
        const status = this._status;

        return html`
            <form @submit=${this._submit}>
                <div class="padded horizontal center-aligning layout border-bottom">
                    <div class="padded stretch semibold">
                        <ptc-ui-icon name="icon.planning.availability"></ptc-ui-icon>
                        ${editing ? "Verfügbarkeit Bearbeiten" : "Neue Verfügbarkeit"}
                    </div>
                    <button type="button" class="slim red transparent" @click=${this._delete} ?hidden=${!editing}>
                        <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                    </button>
                    <button type="button" class="slim transparent" @click=${() => this.done()}>
                        <ptc-ui-icon name="icon.action.close"></ptc-ui-icon>
                    </button>
                </div>

                <div class="double-padded double-spacing vertical layout">
                    <div class="horizontal center-aligning layout">
                        <div
                            class="horizontally-padded stretch"
                            style="--color-highlight: ${availabilityColor(status)}"
                        >
                            <div class="big colored-text">
                                <strong>${formatWeekDayShort(date)}</strong>, ${formatDate(date)}
                            </div>
                            ${this._availability.updated > new Date("1970-01-01")
                                ? html`
                                      <div
                                          class="smaller subtle semibold spacing horizontal layout"
                                          title="Zuletzt bearbeitet am ${formatDate(this._availability.updated)}"
                                      >
                                          <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon> ${formatDate(
                                              this._availability.updated
                                          )}
                                          ${toTimeString(this._availability.updated)}
                                      </div>
                                  `
                                : ""}
                        </div>

                        <div class="padded box">
                            <ptc-ui-icon name="icon.object.history"></ptc-ui-icon>
                            <select class="transparent negatively-margined" name="repeats">
                                <option .value=${Repeat.Never}>Einmalig</option>
                                <option .value=${Repeat.Weekly}>Wöchentlich</option>
                                <option .value=${Repeat.Monthly}>Monatlich</option>
                                <option .value=${Repeat.Yearly}>Jährlich</option>
                            </select>
                        </div>
                    </div>

                    <div class="spacing grid">
                        ${Object.values(AvailabilityStatus).map(
                            (s) => html`
                                <button
                                    class="subtle"
                                    type="button"
                                    .value=${s}
                                    style="--color-highlight: ${availabilityColor(s)}"
                                    ?active=${status === s}
                                    @click=${() => (this._status = s)}
                                >
                                    <ptc-ui-icon name="${availabilityIcon(s)}"></ptc-ui-icon> ${availabilityLabel(s)}
                                </button>
                            `
                        )}
                    </div>

                    <ptc-checkbox-button
                        label="Ganztägig"
                        .buttonClass=${"ghost"}
                        .checked=${live(this._fullDay)}
                        @change=${this._fullDayToggled}
                    ></ptc-checkbox-button>

                    <ptc-ui-collapse .collapsed=${this._fullDay}>
                        <div class="horizontal spacing stretching centering layout">
                            <ptc-ui-time-input compact name="start"></ptc-ui-time-input>
                            <ptc-ui-icon name="icon.object.range-separator"></ptc-ui-icon>
                            <ptc-ui-time-input compact name="end"></ptc-ui-time-input>
                        </div>
                    </ptc-ui-collapse>

                    <div class="field">
                        <label><ptc-ui-icon name="icon.object.comment"></ptc-ui-icon>Kommentar</label>
                        <textarea name="comment" placeholder="Optional"></textarea>
                    </div>

                    <div class="evenly stretching spacing horizontal layout">
                        <button class="primary">Speichern</button>
                        <button type="button" class="transparent" @click=${() => this.done()}>Abbrechen</button>
                    </div>
                </div>
            </form>
        `;
    }
}
